import React, { useState, useEffect, useRef } from "react";
import logo from "../icon/logo.png";

/* ─────────────────────────────────────────────
   DESIGN TOKENS — "Optionstock Institutional"
   Cool, light-institutional system: ice-blue paper,
   deep navy typography, emerald accent, steel-navy
   for rank, true green/red for gains/losses.
───────────────────────────────────────────── */
const C = {
  bg:          "#F3F6FA",
  surface:     "#FFFFFF",
  surfaceAlt:  "#E9EFF5",
  border:      "#D8E2EC",
  borderHover: "#B9C9DA",
  accent:      "#0E7C5A",
  accentDim:   "rgba(14,124,90,0.08)",
  accentGlow:  "rgba(14,124,90,0.18)",
  gold:        "#1E4976",
  goldDim:     "rgba(30,73,118,0.10)",
  text:        "#16283F",
  textMid:     "#4C6178",
  textDim:     "#8C9BAC",
  red:         "#C0392B",
  green:       "#16915F",
  blue:        "#2F6690",
};

/* ── Hero-only palette: "Neural Capital" ──
   Dark, high-signal system used just for the
   hero section so it reads as dynamic/AI-driven
   while the rest of the page keeps the ledger look. */
const HC = {
  bg:        "#06070B",
  surface:   "#0D0F16",
  surfaceAlt:"#12141D",
  border:    "#1D212C",
  text:      "#E9EBF2",
  textMid:   "#9AA0B4",
  textDim:   "#5C6178",
  violet:    "#7C5CFF",
  violetDim: "rgba(124,92,255,0.25)",
  cyan:      "#22D3EE",
  green:     "#34D399",
  red:       "#F87171",
};

/* ─────────────────────────────────────────────
   GLOBAL STYLES  (injected once via <style>)
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;0,9..144,800;0,9..144,900;1,9..144,400;1,9..144,500&family=Public+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=IBM+Plex+Mono:wght@400;500;600&family=Space+Grotesk:wght@400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

html { scroll-behavior: smooth; }

html, body {
  background: ${C.bg};
  color: ${C.text};
  font-family: 'Public Sans', sans-serif;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
  overflow-y: auto;
  height: auto;
}

a { text-decoration: none; color: inherit; }

/* ── Keyframes ── */
@keyframes tickerScroll {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes pulse {
  0%,100% { opacity:1; transform:scale(1); }
  50%      { opacity:.55; transform:scale(.92); }
}
@keyframes ruleFade {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes chartDraw {
  from { stroke-dashoffset: 600; }
  to   { stroke-dashoffset: 0; }
}
@keyframes cookieSlide {
  from { transform: translateX(-50%) translateY(120%); opacity: 0; }
  to   { transform: translateX(-50%) translateY(0);    opacity: 1; }
}
@keyframes mobileMenuOpen {
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Hero entry animations ── */
.hero-badge  { animation: fadeUp .6s cubic-bezier(.22,1,.36,1) .05s both; }
.hero-title  { animation: fadeUp .7s cubic-bezier(.22,1,.36,1) .12s both; }
.hero-sub    { animation: fadeUp .7s cubic-bezier(.22,1,.36,1) .22s both; }
.hero-cta    { animation: fadeUp .7s cubic-bezier(.22,1,.36,1) .32s both; }
.hero-stats  { animation: fadeUp .7s cubic-bezier(.22,1,.36,1) .42s both; }
.hero-card   { animation: fadeUp .8s cubic-bezier(.22,1,.36,1) .26s both; }

.chart-line {
  stroke-dasharray: 600;
  animation: chartDraw 1.8s ease-out .9s both;
}

/* ── Neural hero (dark, AI-reads-the-tape visual) ── */
@keyframes nhGlow  { 0%,100% { opacity:.55; } 50% { opacity:1; } }
@keyframes nhDraw  { from { stroke-dashoffset: 520; } to { stroke-dashoffset: 0; } }
@keyframes nhGrow  { from { transform: scaleY(0); } to { transform: scaleY(1); } }
@keyframes nhTick  { from { opacity:0; transform: translateY(4px); } to { opacity:1; transform: translateY(0); } }
@keyframes nhFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }

/* ── Mobile app showcase ── */
@keyframes phoneFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-14px); } }
.phone-float { animation: phoneFloat 5.2s ease-in-out infinite; }

.nh-line   { stroke-dasharray: 520; animation: nhDraw 1.7s ease-out .7s both; }
.nh-node   { animation: nhGlow 2.4s ease-in-out infinite; }
.nh-candle { transform-origin: bottom; animation: nhGrow .6s cubic-bezier(.22,1,.36,1) both; }

.nh-btn-primary {
  background: linear-gradient(135deg, ${HC.violet}, ${HC.cyan});
  color: #06070B; border: none;
  transition: transform .2s ease, box-shadow .2s ease;
}
.nh-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 28px ${HC.violetDim}; }
.nh-btn-secondary { border: 1px solid ${HC.border} !important; color: ${HC.text} !important; }

/* ── Scroll reveal ── */
.reveal {
  opacity: 0;
  transform: translateY(22px);
  transition: opacity .6s ease, transform .6s ease;
}
.reveal.visible {
  opacity: 1;
  transform: translateY(0);
}

/* ── Buttons ── */
.btn-primary {
  display: inline-flex; align-items: center; gap: 8px;
  background: ${C.accent}; color: #F3FAF7;
  padding: 13px 24px; border-radius: 3px;
  font-size: 13px; font-weight: 600; font-family: 'Public Sans', sans-serif;
  letter-spacing: .02em;
  cursor: pointer; border: 1px solid ${C.accent};
  transition: transform .15s, box-shadow .15s, background .15s;
  position: relative; white-space: nowrap;
}
.btn-primary:hover { transform: translateY(-1px); box-shadow: 0 8px 20px ${C.accentGlow}; background: #0A5F45; }

.btn-secondary {
  display: inline-flex; align-items: center; gap: 8px;
  background: transparent; color: ${C.text};
  padding: 12px 24px; border-radius: 3px;
  font-size: 13px; font-weight: 600; font-family: 'Public Sans', sans-serif;
  letter-spacing: .02em;
  cursor: pointer; border: 1px solid ${C.text};
  transition: background .15s, color .15s;
  white-space: nowrap;
}
.btn-secondary:hover { background: ${C.text}; color: ${C.bg}; }

/* ── Nav links ── */
.nav-link {
  font-size: 12px; color: ${C.textMid};
  font-weight: 600; transition: color .15s;
  letter-spacing: .07em; text-transform: uppercase;
}
.nav-link:hover { color: ${C.accent}; }

/* ── Cards ── */
.trader-card {
  background: ${C.surface}; border: 1px solid ${C.border};
  border-radius: 4px; padding: 26px;
  transition: border-color .2s, box-shadow .2s;
  cursor: pointer; position: relative;
}
.trader-card:hover { border-color: ${C.borderHover}; box-shadow: 0 10px 28px rgba(22,40,63,.08); }

.feature-row {
  border-top: 1px solid ${C.border};
  padding: 26px 0; display: flex; gap: 20px; align-items: flex-start;
  transition: padding-left .2s, background .2s;
}
.feature-row:hover { padding-left: 8px; background: rgba(122,37,48,.03); }
.feature-row:last-child { border-bottom: 1px solid ${C.border}; }

.testimonial-block {
  padding: 8px 0;
}

/* ── Misc UI ── */
.section-label {
  display: inline-flex; align-items: center; gap: 10px;
  font-size: 11px; font-weight: 700;
  letter-spacing: .16em; text-transform: uppercase;
  color: ${C.accent}; margin-bottom: 14px;
  font-family: 'IBM Plex Mono', monospace;
}
.section-label::before { content:''; width: 22px; height: 1px; background: ${C.accent}; display:inline-block; }

.tag {
  display: inline-flex; align-items: center; gap: 7px;
  background: transparent; border: 1px solid ${C.text};
  border-radius: 2px; padding: 6px 14px;
  font-size: 10px; color: ${C.text}; font-weight: 700;
  letter-spacing: .1em; text-transform: uppercase;
  font-family: 'IBM Plex Mono', monospace;
}

.badge-live {
  display: inline-flex; align-items: center; gap: 6px;
  background: transparent; border: 1px solid ${C.accent};
  border-radius: 2px; padding: 4px 9px;
  font-family: 'IBM Plex Mono', monospace; font-size: 10px;
  color: ${C.accent}; font-weight: 600; letter-spacing:.08em;
}
.badge-live::before {
  content: ''; width: 5px; height: 5px; border-radius: 50%;
  background: ${C.accent}; animation: pulse 1.5s ease-in-out infinite;
  flex-shrink: 0;
}

.copy-btn {
  background: transparent; border: 1px solid ${C.accent};
  border-radius: 2px; padding: 7px 15px;
  font-size: 11px; font-weight: 700; color: ${C.accent};
  letter-spacing: .04em;
  cursor: pointer; transition: background .15s, color .15s;
  font-family: 'IBM Plex Mono', monospace;
}
.copy-btn:hover { background: ${C.accent}; color: #F3FAF7; }

.avatar {
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-weight: 700; flex-shrink: 0; font-family: 'Fraunces', serif;
  border: 1px solid rgba(0,0,0,.08);
}

.divider {
  width: 100%; height: 1px;
  background: ${C.border};
}

.grid-bg {
  position: absolute; inset: 0;
  background-image: repeating-linear-gradient(${C.border} 0px, ${C.border} 1px, transparent 1px, transparent 34px);
  opacity: .5;
  mask-image: radial-gradient(ellipse 80% 60% at 50% 0%, black 0%, transparent 100%);
}

.watermark-letter {
  position: absolute; font-family: 'Fraunces', serif; font-weight: 700;
  color: ${C.text}; opacity: .05; pointer-events: none; line-height: 1; user-select: none;
}

/* ── iOS PWA safe area (notch / dynamic island) ── */
@supports (padding-top: env(safe-area-inset-top)) {
  .nav-safe {
    padding-top: env(safe-area-inset-top);
    height: calc(58px + env(safe-area-inset-top));
  }
  .mobile-menu-safe {
    top: calc(58px + env(safe-area-inset-top)) !important;
  }
}

/* ── Cookie banner ── */
.cookie-banner {
  position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%);
  width: calc(100% - 40px); max-width: 720px; z-index: 999;
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 4px; padding: 20px 24px;
  box-shadow: 0 20px 50px rgba(22,40,63,.18);
  animation: cookieSlide .5s cubic-bezier(.22,1,.36,1) .8s both;
  display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
}

/* ── Mobile menu ── */
.mobile-menu {
  animation: mobileMenuOpen .2s ease both;
  position: absolute; top: 100%; left: 0; right: 0;
  background: ${C.bg};
  border-bottom: 1px solid ${C.border};
  padding: 16px 24px 20px;
  display: flex; flex-direction: column; gap: 4px;
}
.mobile-menu .nav-link {
  padding: 12px 0; font-size: 14px;
  border-bottom: 1px solid ${C.border};
}
.mobile-menu .nav-link:last-of-type { border-bottom: none; }

/* ── Input ── */
.tf-input {
  background: transparent; border: none; border-bottom: 1px solid ${C.text};
  border-radius: 0; padding: 12px 4px;
  font-size: 14px; color: ${C.text}; font-family: 'Public Sans', sans-serif;
  outline: none; transition: border-color .2s;
  width: 100%;
}
.tf-input:focus { border-color: ${C.accent}; }
.tf-input::placeholder { color: ${C.textDim}; }

/* ── Scrollbar ── */
::-webkit-scrollbar { width: 6px; }
::-webkit-scrollbar-track { background: ${C.bg}; }
::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 3px; }

/* ── Footer link ── */
.footer-link { font-size: 13px; color: ${C.textMid}; transition: color .15s; }
.footer-link:hover { color: ${C.accent}; }

/* ── Language switcher ── */
.lang-switcher {
  position: relative; display: inline-flex; align-items: center;
}
.lang-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: transparent; border: 1px solid ${C.border};
  border-radius: 2px; padding: 6px 10px;
  font-size: 11px; font-weight: 600; color: ${C.textMid};
  cursor: pointer; font-family: 'IBM Plex Mono', monospace;
  transition: border-color .2s, color .2s;
  white-space: nowrap;
}
.lang-btn:hover { border-color: ${C.borderHover}; color: ${C.text}; }
.lang-dropdown {
  position: absolute; top: calc(100% + 6px); right: 0;
  background: ${C.surface}; border: 1px solid ${C.border};
  border-radius: 4px; padding: 6px;
  min-width: 180px; max-height: 340px; overflow-y: auto; z-index: 400;
  box-shadow: 0 16px 40px rgba(22,40,63,.18);
  animation: mobileMenuOpen .2s ease both;
}
.lang-option {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 12px; border-radius: 2px;
  font-size: 13px; color: ${C.textMid};
  cursor: pointer; font-family: 'Public Sans', sans-serif;
  transition: background .15s, color .15s;
}
.lang-option:hover { background: ${C.surfaceAlt}; color: ${C.text}; }
.lang-option.active { color: ${C.accent}; background: ${C.accentDim}; }

/* Hide Google's default translate bar/badge */
.goog-te-banner-frame, #goog-gt-tt, .goog-te-balloon-frame { display: none !important; }
body { top: 0 !important; }
.skiptranslate { display: none !important; }

/* ═══════════════════════════════════════
   RESPONSIVE BREAKPOINTS
═══════════════════════════════════════ */

/* Tablet  ≤ 900px */
@media (max-width: 900px) {
  .hero-grid    { grid-template-columns: 1fr !important; gap: 44px !important; }
  .hero-card    { max-width: 480px; margin: 0 auto; }
  .mobile-grid  { grid-template-columns: 1fr !important; gap: 40px !important; }
  .traders-grid { grid-template-columns: 1fr 1fr !important; }
  .footer-grid  { grid-template-columns: 1fr 1fr !important; }
  .steps-grid   { gap: 20px !important; }
  .steps-line   { display: none !important; }
  .hero-section { padding: 56px 24px 44px !important; }
  .section-pad  { padding: 72px 24px !important; }
  .nav-links    { display: none !important; }
  .nav-cta-desktop { display: none !important; }
  .hamburger    { display: flex !important; }
  .stats-grid   { grid-template-columns: repeat(2,1fr) !important; }
  .masthead-date{ display: none !important; }
}

/* Mobile  ≤ 600px */
@media (max-width: 600px) {
  .traders-grid  { grid-template-columns: 1fr !important; }
  .steps-grid    { grid-template-columns: 1fr !important; }
  .testi-grid    { grid-template-columns: 1fr !important; }
  .footer-grid   { grid-template-columns: 1fr !important; }
  .cta-email-row { flex-direction: column !important; }
  .cta-email-row input { width: 100% !important; }
  .hero-btns     { flex-direction: column !important; align-items: stretch !important; }
  .hero-btns a, .hero-btns button { text-align: center; justify-content: center; }
  .stats-grid    { grid-template-columns: repeat(2,1fr) !important; }
  .section-pad   { padding: 56px 16px !important; }
  .hero-section  { padding: 44px 16px 36px !important; }
  .footer-bottom { flex-direction: column !important; gap: 12px !important; text-align: center; }
  .feature-row   { flex-direction: column !important; gap: 10px !important; }
}
`;

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const TICKER = [
  { sym:"BTC", price:"$67,420", chg:"+2.31%", up:true  },
  { sym:"ETH", price:"$3,841",  chg:"+1.82%", up:true  },
  { sym:"SOL", price:"$178.4",  chg:"+4.07%", up:true  },
  { sym:"BNB", price:"$592.1",  chg:"-0.54%", up:false },
  { sym:"XRP", price:"$0.631",  chg:"+1.20%", up:true  },
  { sym:"ADA", price:"$0.441",  chg:"-1.10%", up:false },
  { sym:"MATIC",price:"$0.884", chg:"+3.42%", up:true  },
  { sym:"AVAX", price:"$38.71", chg:"+2.75%", up:true  },
  { sym:"LINK", price:"$14.92", chg:"-0.33%", up:false },
  { sym:"DOT",  price:"$7.28",  chg:"+0.91%", up:true  },
];

const TRADERS = [
  { id:"alexei-voronov",  name:"Alexei Voronov", handle:"@voronov_trades", roi:"+347%", monthly:"+28.4%", dd:"4.2%", followers:"12.4K", win:"73%", spark:[30,45,38,55,42,68,60,78,72,95], initials:"AV", bg:"#33405A", star:true },
  { id:"priya-sharma",    name:"Priya Sharma",   handle:"@psharma_fx",     roi:"+218%", monthly:"+19.1%", dd:"2.8%", followers:"8.7K",  win:"81%", spark:[40,35,50,46,60,55,72,68,80,88], initials:"PS", bg:"#2E4A38" },
  { id:"marcus-webb",     name:"Marcus Webb",    handle:"@webb_quant",     roi:"+412%", monthly:"+31.7%", dd:"5.6%", followers:"21.1K", win:"69%", spark:[20,38,30,55,65,58,78,82,70,98], initials:"MW", bg:"#2F5468" },
];

const FEATURES = [
  { icon:"◎", title:"AI Copy Trading",     desc:"Smart algorithms mirror elite traders' moves with adjustable risk controls and position sizing.", href:"/copy-trading" },
  { icon:"◈", title:"Portfolio Analytics", desc:"Deep metrics: Sharpe ratio, max drawdown, correlation heatmaps, and P&L attribution by strategy.", href:"/analytics" },
  { icon:"◇", title:"Hire Pro Traders",    desc:"Curated marketplace of verified traders. Review audited track records before allocating capital.", href:"/market-place" },
  { icon:"◉", title:"Risk Management",     desc:"Set stop-loss thresholds, daily loss limits, and exposure caps at account or per-trader level.", href:"/market-place" },
  { icon:"▣", title:"Multi-Asset Support", desc:"Trade crypto, forex, stocks, and commodities from one unified dashboard with cross-asset analytics.", href:"/market-place" },
  { icon:"◐", title:"Instant Settlements", desc:"Near-instant settlement with full audit trail. Withdraw anytime with zero lockup periods.", href:"/market-place" },
];

const TESTIMONIALS = [
  { quote:"Optionstock's copy system is the most transparent I've used. I can see exactly what trades fire and why.", author:"James K.", role:"Retail Investor · London",           initials:"JK", bg:"#33405A" },
  { quote:"ROI up 180% in 7 months. The risk controls saved me during the March dip — didn't lose a cent I didn't plan.", author:"Sofia R.", role:"Portfolio Manager · São Paulo", initials:"SR", bg:"#2E4A38" },
  { quote:"The pro trader marketplace is a game changer. Audited records mean I know exactly what I'm buying into.", author:"Tariq M.", role:"Family Office · Dubai",             initials:"TM", bg:"#2F5468" },
];

/* ─────────────────────────────────────────────
   MINI COMPONENTS
───────────────────────────────────────────── */
const Sparkline = ({ data, up }) => {
  const W=80, H=32;
  const mn=Math.min(...data), mx=Math.max(...data);
  const pts = data.map((v,i)=>`${(i/(data.length-1))*W},${H-((v-mn)/(mx-mn))*H}`).join(" ");
  const fill = `${pts} ${W},${H} 0,${H}`;
  const col = up ? C.green : C.red;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none" style={{flexShrink:0}}>
      <polygon points={fill} fill={`${col}14`} />
      <polyline points={pts} stroke={col} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
};

const HeroChart = () => (
  <svg width="100%" height="110" viewBox="0 0 320 110" fill="none" preserveAspectRatio="none">
    <defs>
      <linearGradient id="hg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor={C.accent} stopOpacity=".18"/>
        <stop offset="100%" stopColor={C.accent} stopOpacity="0"/>
      </linearGradient>
    </defs>
    <path d="M0,95 L20,88 L45,75 L65,80 L85,65 L105,70 L120,55 L140,45 L160,52 L175,38 L195,30 L215,20 L235,28 L255,15 L275,10 L295,5 L320,8 L320,110 L0,110 Z" fill="url(#hg)"/>
    <path d="M0,95 L20,88 L45,75 L65,80 L85,65 L105,70 L120,55 L140,45 L160,52 L175,38 L195,30 L215,20 L235,28 L255,15 L275,10 L295,5 L320,8"
      stroke={C.accent} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" className="chart-line"/>
    <circle cx="320" cy="8" r="3.5"  fill={C.accent}/>
  </svg>
);

/* Neural hero visual — a small node network "reads" a live
   candlestick chart: pulses travel down converging lines into
   the candles the network is attending to, with a live-ticking
   confidence readout. This is the hero's signature element. */
const NeuralChart = () => {
  const candles = [
    { x: 8,   open: 92,  close: 78,  high: 70,  low: 100 },
    { x: 30,  open: 78,  close: 86,  high: 72,  low: 92 },
    { x: 52,  open: 86,  close: 60,  high: 54,  low: 92 },
    { x: 74,  open: 60,  close: 68,  high: 52,  low: 78 },
    { x: 96,  open: 68,  close: 40,  high: 34,  low: 74 },
    { x: 118, open: 40,  close: 50,  high: 32,  low: 58 },
    { x: 140, open: 50,  close: 24,  high: 18,  low: 56 },
    { x: 162, open: 24,  close: 32,  high: 14,  low: 40 },
    { x: 184, open: 32,  close: 12,  high: 6,   low: 38 },
    { x: 206, open: 12,  close: 20,  high: 4,   low: 30 },
    { x: 228, open: 20,  close: 6,   high: 2,   low: 26 },
  ];
  const nodes = [
    { cx: 60,  cy: 18 },
    { cx: 110, cy: 8 },
    { cx: 160, cy: 14 },
    { cx: 200, cy: 6 },
  ];
  const targets = [96, 140, 184];
  const [conf, setConf] = useState(88);

  useEffect(() => {
    const id = setInterval(() => setConf((c) => Math.min(98, Math.max(80, c + (Math.random() > 0.5 ? 1 : -1)))), 1300);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{ position:"relative" }}>
      <svg width="100%" height="190" viewBox="0 0 250 190" fill="none">
        <defs>
          <linearGradient id="nhGlowGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={HC.violet} stopOpacity="0.9"/>
            <stop offset="100%" stopColor={HC.cyan} stopOpacity="0.4"/>
          </linearGradient>
        </defs>

        {nodes.map((n, i) => {
          const t = targets[i % targets.length];
          const d = `M${n.cx},${n.cy} Q${(n.cx + t) / 2},${n.cy + 35} ${t},34`;
          return (
            <g key={i}>
              <path d={d} stroke={HC.violet} strokeWidth="1" fill="none" opacity="0.35"/>
              <circle r="2.4" fill={HC.cyan}>
                <animateMotion dur={`${2 + i * 0.4}s`} repeatCount="indefinite" path={d}/>
              </circle>
            </g>
          );
        })}

        {nodes.map((n, i) => (
          <g key={i} className="nh-node" style={{ animationDelay:`${i * 0.25}s` }}>
            <circle cx={n.cx} cy={n.cy} r="7" fill="url(#nhGlowGrad)" opacity="0.25"/>
            <circle cx={n.cx} cy={n.cy} r="3" fill={HC.violet}/>
          </g>
        ))}
        <path d={`M${nodes[0].cx},${nodes[0].cy} L${nodes[1].cx},${nodes[1].cy} L${nodes[2].cx},${nodes[2].cy} L${nodes[3].cx},${nodes[3].cy}`}
          stroke={HC.violet} strokeWidth="0.75" opacity="0.3" fill="none"/>

        {candles.map((c, i) => {
          const up = c.close < c.open;
          const bodyTop = Math.min(c.open, c.close);
          const bodyH = Math.max(Math.abs(c.open - c.close), 2);
          const isTarget = targets.includes(c.x);
          const col = isTarget ? HC.cyan : up ? HC.green : HC.red;
          return (
            <g key={i} className="nh-candle" style={{ animationDelay:`${0.7 + i * 0.06}s` }}>
              <line x1={c.x + 7} x2={c.x + 7} y1={c.high} y2={c.low} stroke={col} strokeWidth="1" opacity="0.7"/>
              <rect x={c.x} y={bodyTop + 40} width="14" height={bodyH} fill={col} opacity={isTarget ? 1 : 0.85} rx="1"/>
              {isTarget && <circle cx={c.x + 7} cy={bodyTop + 40} r="9" fill="none" stroke={HC.cyan} strokeWidth="1" opacity="0.5"/>}
            </g>
          );
        })}
      </svg>

      <div style={{
        position:"absolute", top:"-10px", right:"0px",
        background:HC.surfaceAlt, border:`1px solid ${HC.border}`,
        padding:"7px 12px", borderRadius:"6px", display:"flex", alignItems:"center", gap:"8px",
        animation:"nhFloat 3.4s ease-in-out infinite",
      }}>
        <span style={{ width:6, height:6, borderRadius:"50%", background:HC.cyan, display:"inline-block", animation:"nhGlow 1.1s ease-in-out infinite" }}/>
        <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:"10px", color:HC.textMid }}>
          NEURAL CONFIDENCE&nbsp;
          <strong key={conf} style={{ color:HC.cyan, animation:"nhTick .3s ease-out" }}>{conf}%</strong>
        </span>
      </div>
    </div>
  );
};

/* Mobile app showcase — a tilting phone frame with a live
   trading screen: ticking balance, flickering position P&L,
   and a rotating stream of push-style notifications. */
const MobileShowcase = () => {
  const wrapRef = useRef(null);
  const [tilt, setTilt] = useState({ x:0, y:0 });
  const [balance, setBalance] = useState(48231.62);
  const [delta, setDelta] = useState(2.14);
  const [positions, setPositions] = useState([
    { sym:"BTC/USDT", qty:"0.42", pnl:4.8 },
    { sym:"ETH/USDT", qty:"3.10", pnl:-1.2 },
    { sym:"SOL/USDT", qty:"58.0", pnl:6.3 },
  ]);
  const [toastIdx, setToastIdx] = useState(0);
  const toasts = [
    { label:"Trade executed", detail:"BTC/USDT · +$182.40" },
    { label:"Signal fired",   detail:"ETH/USDT · 91% confidence" },
    { label:"Copied trader",  detail:"@webb_quant allocated" },
  ];

  useEffect(() => {
    const id = setInterval(() => {
      setBalance(b => +(b + (Math.random()-0.42) * 60).toFixed(2));
      setDelta(d => +Math.max(-4, Math.min(9, d + (Math.random()-0.45))).toFixed(2));
      setPositions(ps => ps.map(p => ({ ...p, pnl: +Math.max(-9, Math.min(12, p.pnl + (Math.random()-0.48)*1.6)).toFixed(1) })));
    }, 1800);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setToastIdx(i => (i+1) % toasts.length), 3400);
    return () => clearInterval(id);
  }, []);

  const handleMove = (e) => {
    const el = wrapRef.current;
    if(!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: py * -10, y: px * 14 });
  };
  const handleLeave = () => setTilt({ x:0, y:0 });
  const up = delta >= 0;

  return (
    <div
      ref={wrapRef}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ position:"relative", display:"flex", justifyContent:"center", perspective:"1400px" }}
    >
      <div style={{ position:"absolute", width:"320px", height:"320px", borderRadius:"50%", background:C.accent, opacity:.16, filter:"blur(70px)", top:"8%", left:"50%", transform:"translateX(-50%)", pointerEvents:"none" }}/>

      <div className="phone-float" style={{ position:"relative" }}>
        <div style={{
          width:"264px", transform:`rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transition:"transform .35s cubic-bezier(.22,1,.36,1)", transformStyle:"preserve-3d",
        }}>
          <div style={{
            background:"linear-gradient(155deg, #1B2230, #0A0D14)",
            borderRadius:"42px", padding:"14px",
            boxShadow:"0 40px 80px -24px rgba(22,40,63,.45), 0 0 0 1px rgba(255,255,255,.05) inset",
          }}>
            <div style={{ width:"90px", height:"22px", background:"#0A0D14", borderRadius:"14px", margin:"0 auto 10px", border:"1px solid rgba(255,255,255,.06)" }}/>

            <div style={{ background:"linear-gradient(185deg,#0D1520,#080B10)", borderRadius:"28px", padding:"18px 16px 16px", overflow:"hidden", position:"relative" }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:"11px", color:"rgba(233,239,246,.55)", fontFamily:"'IBM Plex Mono',monospace", marginBottom:"20px" }}>
                <span>9:41</span>
                <span>●●●&nbsp;&nbsp;5G</span>
              </div>

              <div style={{ fontSize:"10px", letterSpacing:".08em", textTransform:"uppercase", color:"rgba(233,239,246,.4)", fontFamily:"'IBM Plex Mono',monospace", marginBottom:"6px" }}>Portfolio Value</div>
              <div style={{ marginBottom:"4px" }}>
                <span key={balance} style={{ fontFamily:"'Fraunces',serif", fontSize:"27px", fontWeight:"600", color:"#F3F6FA", animation:"nhTick .3s ease-out" }}>
                  ${balance.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}
                </span>
              </div>
              <div style={{ fontSize:"12px", color: up ? "#4ADE9A" : "#F27C6B", fontFamily:"'IBM Plex Mono',monospace", marginBottom:"18px" }}>
                {up?"▲":"▼"} {Math.abs(delta)}% today
              </div>

              <svg width="100%" height="54" viewBox="0 0 220 54" style={{ marginBottom:"16px", display:"block" }}>
                <polyline points="0,40 22,34 44,38 66,20 88,26 110,14 132,22 154,8 176,16 198,6 220,12"
                  fill="none" stroke={up?"#4ADE9A":"#F27C6B"} strokeWidth="2" className="chart-line"/>
              </svg>

              <div style={{ display:"flex", gap:"6px", marginBottom:"16px" }}>
                {["1D","1W","1M","1Y"].map((t,i)=>(
                  <span key={t} style={{ fontSize:"10px", padding:"4px 10px", borderRadius:"20px", fontFamily:"'IBM Plex Mono',monospace", background: i===0?C.accent:"rgba(255,255,255,.06)", color: i===0?"#08130E":"rgba(233,239,246,.5)" }}>{t}</span>
                ))}
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
                {positions.map((p)=>(
                  <div key={p.sym} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 10px", borderRadius:"10px", background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.06)" }}>
                    <div>
                      <div style={{ fontSize:"12px", color:"#E9EFF6", fontWeight:"600" }}>{p.sym}</div>
                      <div style={{ fontSize:"10px", color:"rgba(233,239,246,.4)", fontFamily:"'IBM Plex Mono',monospace" }}>{p.qty} units</div>
                    </div>
                    <div key={p.pnl} style={{ fontSize:"12px", fontFamily:"'IBM Plex Mono',monospace", color: p.pnl>=0?"#4ADE9A":"#F27C6B", animation:"nhTick .3s ease-out" }}>
                      {p.pnl>=0?"+":""}{p.pnl}%
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display:"flex", justifyContent:"space-around", alignItems:"center", marginTop:"20px", paddingTop:"14px", borderTop:"1px solid rgba(255,255,255,.07)" }}>
                {["◎","◈","◉","◇"].map((ic,i)=>(
                  <span key={i} style={{
                    fontSize: i===2?"18px":"14px", color: i===2? "#08130E": "rgba(233,239,246,.4)",
                    width: i===2?"30px":"20px", height:i===2?"30px":"20px",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    background: i===2?C.accent:"transparent", borderRadius:"50%",
                  }}>{ic}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div key={toastIdx} style={{
          position:"absolute", top:"6%", right:"-6%", maxWidth:"188px",
          background:C.surface, border:`1px solid ${C.border}`, borderRadius:"10px",
          padding:"10px 14px", boxShadow:"0 16px 32px rgba(22,40,63,.16)",
          animation:"fadeUp .5s cubic-bezier(.22,1,.36,1) both",
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:"6px", marginBottom:"3px" }}>
            <span style={{ width:6, height:6, borderRadius:"50%", background:C.accent, display:"inline-block", animation:"nhGlow 1.2s ease-in-out infinite" }}/>
            <span style={{ fontSize:"10px", fontFamily:"'IBM Plex Mono',monospace", color:C.textDim, letterSpacing:".04em", textTransform:"uppercase" }}>{toasts[toastIdx].label}</span>
          </div>
          <div style={{ fontSize:"12px", color:C.text, fontWeight:"600" }}>{toasts[toastIdx].detail}</div>
        </div>
      </div>
    </div>
  );
};

/* Ink rubber-stamp — the page's signature mark, used wherever
   something needs to read as "authenticated" (live, verified, ranked #1) */
const Stamp = ({ label="VERIFIED", sub="OS", color=C.accent, size=58, rotate=-9 }) => {
  const uid = useRef(`stamp-${Math.random().toString(36).slice(2,8)}`);
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ transform:`rotate(${rotate}deg)`, flexShrink:0 }}>
      <defs>
        <filter id={uid.current}>
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="4" result="noise"/>
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="2.6"/>
        </filter>
        <path id={`${uid.current}-path`} d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
      </defs>
      <g filter={`url(#${uid.current})`}>
        <circle cx="50" cy="50" r="45" fill="none" stroke={color} strokeWidth="2.4"/>
        <circle cx="50" cy="50" r="37" fill="none" stroke={color} strokeWidth="1"/>
        <text fontSize="8.4" fontWeight="700" letterSpacing="2.4" fill={color} fontFamily="'IBM Plex Mono', monospace">
          <textPath href={`#${uid.current}-path`} startOffset="1%">{label} • {label} •</textPath>
        </text>
        <text x="50" y="57" textAnchor="middle" fontSize="19" fontWeight="800" fill={color} fontFamily="'Fraunces', serif" fontStyle="italic">{sub}</text>
      </g>
    </svg>
  );
};

/* Scroll-reveal wrapper */
const Reveal = ({ children, delay=0, style }) => {
  const ref = useRef();
  useEffect(()=>{
    const el = ref.current;
    const ob = new IntersectionObserver(([e])=>{ if(e.isIntersecting){ el.classList.add("visible"); ob.disconnect(); }},{ threshold:.08 });
    ob.observe(el);
    return ()=>ob.disconnect();
  },[]);
  return <div ref={ref} className="reveal" style={{ transitionDelay:`${delay}ms`, ...style }}>{children}</div>;
};

/* Smooth scroll link */
const SLink = ({ to, children, className, style, onClick }) => {
  const handle = (e) => {
    e.preventDefault();
    const el = document.getElementById(to);
    if(el) el.scrollIntoView({ behavior:"smooth", block:"start" });
    onClick && onClick();
  };
  return <a href={`#${to}`} className={className} style={style} onClick={handle}>{children}</a>;
};

/* ─────────────────────────────────────────────
   LANGUAGE SWITCHER
   Uses Google Translate Element API (free, no API key).
   Selected language is stored in localStorage as "tf_lang"
   so it persists across all Optionstock pages.
───────────────────────────────────────────── */
const LANGUAGES = [
  { code: "en",    label: "English",    flag: "🇬🇧" },
  { code: "fr",    label: "Français",   flag: "🇫🇷" },
  { code: "es",    label: "Español",    flag: "🇪🇸" },
  { code: "de",    label: "Deutsch",    flag: "🇩🇪" },
  { code: "pt",    label: "Português",  flag: "🇧🇷" },
  { code: "ar",    label: "العربية",    flag: "🇸🇦" },
  { code: "zh-CN", label: "中文",       flag: "🇨🇳" },
  { code: "ja",    label: "日本語",     flag: "🇯🇵" },
  { code: "ru",    label: "Русский",    flag: "🇷🇺" },
  { code: "hi",    label: "हिन्दी",     flag: "🇮🇳" },
  { code: "yo",    label: "Yoruba",     flag: "🇳🇬" },
  { code: "ha",    label: "Hausa",      flag: "🇳🇬" },
  // Newly added
  { code: "pl",    label: "Polski",     flag: "🇵🇱" },
  { code: "fi",    label: "Suomi",      flag: "🇫🇮" },
  { code: "cs",    label: "Čeština",    flag: "🇨🇿" },
  { code: "ga",    label: "Gaeilge",    flag: "🇮🇪" },
  { code: "af",    label: "Afrikaans",  flag: "🇿🇦" },
  { code: "it",    label: "Italiano",   flag: "🇮🇹" },
  { code: "nl",    label: "Nederlands", flag: "🇳🇱" },
  { code: "sv",    label: "Svenska",    flag: "🇸🇪" },
  { code: "no",    label: "Norsk",      flag: "🇳🇴" },
  { code: "da",    label: "Dansk",      flag: "🇩🇰" },
  { code: "el",    label: "Ελληνικά",   flag: "🇬🇷" },
  { code: "tr",    label: "Türkçe",     flag: "🇹🇷" },
  { code: "uk",    label: "Українська", flag: "🇺🇦" },
  { code: "ro",    label: "Română",     flag: "🇷🇴" },
  { code: "hu",    label: "Magyar",     flag: "🇭🇺" },
  { code: "ko",    label: "한국어",     flag: "🇰🇷" },
  { code: "vi",    label: "Tiếng Việt", flag: "🇻🇳" },
  { code: "th",    label: "ไทย",        flag: "🇹🇭" },
  { code: "id",    label: "Indonesia",  flag: "🇮🇩" },
  { code: "tl",    label: "Filipino",   flag: "🇵🇭" },
  { code: "he",    label: "עברית",      flag: "🇮🇱" },
  { code: "sw",    label: "Kiswahili",  flag: "🇰🇪" },
  { code: "bn",    label: "বাংলা",      flag: "🇧🇩" },
  { code: "ur",    label: "اردو",       flag: "🇵🇰" },
];

// Inject the hidden Google Translate element & callback (called once globally)
const initGoogleTranslate = () => {
  if (window._gtInitDone) return;
  window._gtInitDone = true;

  if (!document.getElementById("google_translate_element")) {
    const el = document.createElement("div");
    el.id = "google_translate_element";
    el.style.cssText = "display:none!important;position:absolute;left:-9999px;";
    document.body.appendChild(el);
  }

  window.googleTranslateElementInit = () => {
    // eslint-disable-next-line no-new
    new window.google.translate.TranslateElement(
      { pageLanguage: "en", autoDisplay: false },
      "google_translate_element"
    );
    const saved = localStorage.getItem("tf_lang");
    if (saved && saved !== "en") {
      setTimeout(() => applyGoogleTranslate(saved), 800);
    }
  };

  const script = document.createElement("script");
  script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  script.async = true;
  document.head.appendChild(script);
};

// Programmatically trigger Google Translate via cookie
const applyGoogleTranslate = (langCode) => {
  if (langCode === "en") {
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + window.location.hostname;
    window.location.reload();
    return;
  }
  const pair = "/en/" + langCode;
  document.cookie = "googtrans=" + pair + "; path=/";
  document.cookie = "googtrans=" + pair + "; path=/; domain=" + window.location.hostname;
  const sel = document.querySelector(".goog-te-combo");
  if (sel) {
    sel.value = langCode;
    sel.dispatchEvent(new Event("change"));
  } else {
    window.location.reload();
  }
};

const LanguageSwitcher = () => {
  const [open, setOpen]       = useState(false);
  const [current, setCurrent] = useState(() => localStorage.getItem("tf_lang") || "en");
  const ref                   = useRef(null);

  useEffect(() => { initGoogleTranslate(); }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (code) => {
    setCurrent(code);
    localStorage.setItem("tf_lang", code);
    setOpen(false);
    applyGoogleTranslate(code);
  };

  const active = LANGUAGES.find(l => l.code === current) || LANGUAGES[0];

  return (
    <div className="lang-switcher" ref={ref}>
      <button className="lang-btn" onClick={() => setOpen(o => !o)} title="Change language">
        <span style={{ fontSize:"13px" }}>{active.flag}</span>
        <span>{active.label}</span>
        <span style={{ fontSize:"9px", opacity:.6, marginLeft:"2px" }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="lang-dropdown">
          <div style={{ fontSize:"10px", fontWeight:"700", color:C.textDim, letterSpacing:".08em", textTransform:"uppercase", padding:"4px 12px 8px", fontFamily:"'IBM Plex Mono',monospace" }}>
            Select Language
          </div>
          {LANGUAGES.map(l => (
            <div
              key={l.code}
              className={"lang-option" + (l.code === current ? " active" : "")}
              onClick={() => select(l.code)}
            >
              <span style={{ fontSize:"16px" }}>{l.flag}</span>
              <span>{l.label}</span>
              {l.code === current && <span style={{ marginLeft:"auto", fontSize:"11px" }}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   COOKIE BANNER
───────────────────────────────────────────── */
const CookieBanner = () => {
  const [state, setState] = useState(null); // null=loading, 'show', 'hidden'
  const [saving, setSaving] = useState(false);

  useEffect(()=>{
    const choice = localStorage.getItem("tf_cookie_consent");
    setState(choice ? "hidden" : "show");
  },[]);

  const accept = () => {
    setSaving(true);
    setTimeout(()=>{
      localStorage.setItem("tf_cookie_consent","accepted");
      setState("hidden");
    }, 300);
  };
  const decline = () => {
    localStorage.setItem("tf_cookie_consent","declined");
    setState("hidden");
  };

  if(state !== "show") return null;

  return (
    <div className="cookie-banner" style={{ opacity: saving ? 0 : 1, transition:"opacity .3s" }}>
      <div style={{ fontFamily:"'Fraunces',serif", fontStyle:"italic", fontSize:"26px", color:C.accent, flexShrink:0 }}>§</div>

      <div style={{ flex:1, minWidth:"200px" }}>
        <div style={{ fontSize:"14px", fontWeight:"700", color:C.text, marginBottom:"4px", fontFamily:"'Fraunces',serif" }}>
          A note on cookies
        </div>
        <p style={{ fontSize:"12px", color:C.textMid, lineHeight:"1.6" }}>
          We use cookies to personalise content, analyse traffic, and improve your experience.
          Read our{" "}
          <a href="/cookie" style={{ color:C.accent, textDecoration:"underline" }}>Cookie Policy</a>
          {" "}for more details.
        </p>
      </div>

      <div style={{ display:"flex", gap:"10px", flexShrink:0, flexWrap:"wrap" }}>
        <button onClick={decline} style={{
          padding:"9px 18px", borderRadius:"3px", fontSize:"13px", fontWeight:"600",
          background:"transparent", border:`1px solid ${C.border}`, color:C.textMid,
          cursor:"pointer", fontFamily:"'Public Sans',sans-serif", whiteSpace:"nowrap",
          transition:"border-color .2s, color .2s",
        }}
          onMouseEnter={e=>{ e.currentTarget.style.borderColor=C.borderHover; e.currentTarget.style.color=C.text; }}
          onMouseLeave={e=>{ e.currentTarget.style.borderColor=C.border; e.currentTarget.style.color=C.textMid; }}
        >Decline</button>
        <button onClick={accept} className="btn-primary" style={{ padding:"9px 20px", fontSize:"13px" }}>
          Accept All
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
const OptionstockLanding = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [email, setEmail]       = useState("");
  const [submitted, setSubmitted] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  const handleSubmit = () => { if(email.includes("@")) setSubmitted(true); };

  /* Inject Chatway live-chat widget once */
  useEffect(() => {
    if (document.getElementById("chatway-widget-script")) return;
    const script = document.createElement("script");
    script.id = "chatway-widget-script";
    script.src = "https://cdn.chatway.app/widget.js?id=vK7FysdMzgk1";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const tickerAll = [...TICKER, ...TICKER];
  const NAV_ITEMS = [
    { label:"Platform",     id:"features"     },
    { label:"Copy Trading", id:"traders"       },
    { label:"Mobile",       id:"mobile-app"    },
    { label:"Hire Trader",  id:"hire-trader"   },
    { label:"Forecast",     id:"forecast"      },
    { label:"Calendar",     id:"calendar"      },
    { label:"Security",     id:"security"      },
  ];

  const today = new Date();
  const dateline = today.toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric", year:"numeric" }).toUpperCase();

  return (
    <>
      <style>{CSS}</style>

      {/* ══ MASTHEAD STRIP ══ */}
      <div className="masthead-date" style={{ background:C.text, color:C.bg, fontFamily:"'IBM Plex Mono',monospace", fontSize:"10.5px", letterSpacing:".07em", padding:"6px clamp(16px,4vw,40px)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <span>{dateline} · GLOBAL EDITION</span>
        <span className="badge-live" style={{ borderColor:C.bg, color:C.bg }}>MARKETS LIVE</span>
      </div>

      {/* ══ NAVBAR ══ */}
      <nav className="nav-safe" style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"0 clamp(16px,4vw,40px)", height:"58px",
        background:`${C.bg}F2`, backdropFilter:"blur(10px)",
        borderBottom:`1px solid ${C.border}`,
        position:"sticky", top:0, zIndex:300,
        WebkitBackdropFilter:"blur(10px)",
      }}>
        {/* Logo */}
        <SLink to="hero" style={{ display:"flex", alignItems:"center" }}>
          <img src={logo} alt="Optionstock" style={{ height:"32px", width:"auto" }} />
        </SLink>

        {/* Desktop links */}
        <div className="nav-links" style={{ display:"flex", gap:"30px" }}>
          {NAV_ITEMS.map(n => (
            <SLink key={n.id} to={n.id} className="nav-link">{n.label}</SLink>
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="nav-cta-desktop" style={{ display:"flex", gap:"10px", alignItems:"center" }}>
          <LanguageSwitcher />
          <a href="/login"  style={{ fontSize:"12px", padding:"9px 16px", borderRadius:"3px", border:`1px solid ${C.border}`, color:C.textMid, fontWeight:"600" }}>Log in</a>
          <a href="/signup" className="btn-primary" style={{ padding:"9px 18px", fontSize:"12px" }}>Get Started →</a>
        </div>

        {/* Hamburger */}
        <button className="hamburger" onClick={()=>setMenuOpen(o=>!o)} style={{
          display:"none", flexDirection:"column", gap:"5px", background:"none",
          border:"none", cursor:"pointer", padding:"6px",
        }}>
          {[0,1,2].map(i=>(
            <span key={i} style={{
              display:"block", width:"22px", height:"2px", background:C.text, borderRadius:"1px",
              transform: menuOpen && i===0 ? "rotate(45deg) translate(5px,5px)" :
                         menuOpen && i===1 ? "scaleX(0)" :
                         menuOpen && i===2 ? "rotate(-45deg) translate(5px,-5px)" : "none",
              transition:"transform .2s",
            }}/>
          ))}
        </button>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="mobile-menu mobile-menu-safe" style={{ position:"fixed", top:"58px", zIndex:299 }}>
          {NAV_ITEMS.map(n=>(
            <SLink key={n.id} to={n.id} className="nav-link" onClick={closeMenu}>{n.label}</SLink>
          ))}
          <div style={{ padding:"12px 0 4px" }}>
            <LanguageSwitcher />
          </div>
          <div style={{ display:"flex", gap:"10px", marginTop:"10px" }}>
            <a href="/login"  className="btn-secondary" style={{flex:1, justifyContent:"center", fontSize:"13px", padding:"10px"}}>Log in</a>
            <a href="/signup" className="btn-primary"   style={{flex:1, justifyContent:"center", fontSize:"13px", padding:"10px"}}>Get Started</a>
          </div>
        </div>
      )}

      {/* ══ TICKER TAPE ══ */}
      <div style={{ height:"32px", background:C.text, borderBottom:`1px solid ${C.border}`, overflow:"hidden", display:"flex", alignItems:"center" }}>
        <div style={{ display:"inline-flex", animation:"tickerScroll 50s linear infinite", whiteSpace:"nowrap" }}>
          {tickerAll.map((t,i)=>(
            <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:"7px", padding:"0 20px", fontSize:"11px" }}>
              <span style={{ color:C.bg,    fontFamily:"'IBM Plex Mono',monospace", fontWeight:"600" }}>{t.sym}</span>
              <span style={{ color:`${C.bg}99`, fontFamily:"'IBM Plex Mono',monospace" }}>{t.price}</span>
              <span style={{ color:t.up?"#8FBF9E":"#D69488", fontFamily:"'IBM Plex Mono',monospace" }}>{t.chg}</span>
              <span style={{ color:`${C.bg}44` }}>◆</span>
            </span>
          ))}
        </div>
      </div>

      {/* ══ HERO — "Neural Capital" (dark, AI reads the tape) ══ */}
      <section id="hero" className="hero-section" style={{
        position:"relative", minHeight:"calc(100vh - 130px)",
        display:"flex", alignItems:"center",
        overflow:"hidden", padding:"72px clamp(16px,5vw,60px)",
        background:`linear-gradient(180deg, ${HC.bg} 0%, ${HC.bg} 88%, ${C.bg} 100%)`,
      }}>
        <div className="watermark-letter" style={{ fontSize:"clamp(280px,38vw,560px)", top:"-8%", right:"-4%", color:HC.surface }}>O</div>

        <div className="hero-grid" style={{
          position:"relative", zIndex:1,
          display:"grid", gridTemplateColumns:"1.1fr 0.9fr",
          gap:"60px", maxWidth:"1200px", margin:"0 auto", width:"100%", alignItems:"center",
        }}>
          {/* Left */}
          <div>
            <div className="hero-badge" style={{
              display:"inline-flex", alignItems:"center", gap:"8px", marginBottom:"22px",
              fontFamily:"'IBM Plex Mono',monospace", fontSize:"11px", letterSpacing:".14em",
              color:HC.cyan, textTransform:"uppercase", border:`1px solid ${HC.border}`,
              padding:"6px 12px", borderRadius:"20px", background:HC.surface,
            }}>
              <span style={{ width:6, height:6, borderRadius:"50%", background:HC.violet, display:"inline-block", animation:"nhGlow 1.2s ease-in-out infinite" }}/>
              Neural signal engine — live
            </div>

            <h1 className="hero-title" style={{
              fontFamily:"'Fraunces',serif",
              fontSize:"clamp(38px,5.8vw,72px)",
              fontWeight:"600", lineHeight:"1.04",
              letterSpacing:"-0.5px", marginBottom:"22px", color:HC.text,
            }}>
              The market,<br/>
              <span style={{
                fontStyle:"italic", fontWeight:"500",
                background:`linear-gradient(120deg, ${HC.violet}, ${HC.cyan})`,
                WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
              }}>read by a neural net.</span>
            </h1>

            <p className="hero-sub" style={{ fontSize:"clamp(15px,1.8vw,17px)", color:HC.textMid, lineHeight:"1.7", marginBottom:"36px", maxWidth:"480px" }}>
              Optionstock's engine watches every candle as it forms, scoring signals in real time
              so you copy trades with a live confidence score attached. Join <strong style={{color:HC.text}}>280,000+</strong> investors trading alongside the model.
            </p>

            <div className="hero-cta hero-btns" style={{ display:"flex", gap:"12px", flexWrap:"wrap", marginBottom:"44px" }}>
              <a href="/signup" className="btn-primary nh-btn-primary">Start for Free <span style={{fontSize:"16px"}}>→</span></a>
              <a href="#how-it-works" className="btn-secondary nh-btn-secondary" onClick={e=>{ e.preventDefault(); document.getElementById("how-it-works")?.scrollIntoView({behavior:"smooth"}); }}>▶ Watch it think</a>
            </div>

            {/* Stats */}
            <div className="hero-stats stats-grid" style={{
              display:"grid", gridTemplateColumns:"repeat(4,1fr)",
              borderTop:`1px solid ${HC.border}`, borderBottom:`1px solid ${HC.border}`,
            }}>
              {[{val:"$4.2B",lbl:"Volume"},{val:"280K",lbl:"Traders"},{val:"94%",lbl:"Signal Acc."},{val:"2,400+",lbl:"Pros"}].map((s,i)=>(
                <div key={i} style={{
                  padding:"16px 10px", textAlign:"center",
                  borderRight: i<3 ? `1px solid ${HC.border}` : "none",
                }}>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(18px,2vw,24px)", color:HC.text, fontWeight:"600", marginBottom:"3px" }}>{s.val}</div>
                  <div style={{ fontSize:"10px", color:HC.textDim, letterSpacing:".08em", textTransform:"uppercase", fontFamily:"'IBM Plex Mono',monospace" }}>{s.lbl}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero Card — the neural read */}
          <div className="hero-card" style={{ position:"relative" }}>
            <div style={{
              background:`linear-gradient(180deg, ${HC.surface}, ${HC.surfaceAlt})`,
              border:`1px solid ${HC.border}`, borderRadius:"10px",
              padding:"clamp(20px,3vw,28px)", boxShadow:`0 30px 60px -20px ${HC.violetDim}`,
            }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"18px", flexWrap:"wrap", gap:"10px" }}>
                <div>
                  <div style={{ fontSize:"11px", color:HC.textDim, marginBottom:"4px", fontFamily:"'IBM Plex Mono',monospace", letterSpacing:".05em" }}>ANALYZING</div>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(22px,3vw,30px)", color:HC.text, fontWeight:"600" }}>ETH/USDT</div>
                </div>
                <div style={{ textAlign:"right", fontFamily:"'IBM Plex Mono',monospace", fontSize:"12px", color:HC.red }}>-4.12% ▼</div>
              </div>

              <NeuralChart/>
            </div>

            {/* Floating badges */}
            <div style={{ position:"absolute", top:"-14px", right:"18px", background:HC.surface, border:`1px solid ${HC.border}`, borderRadius:"8px", padding:"8px 12px" }}>
              <div style={{ fontSize:"9px", color:HC.textDim, marginBottom:"2px", fontFamily:"'IBM Plex Mono',monospace" }}>NODES ACTIVE</div>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:"16px", color:HC.cyan, fontWeight:"600" }}>512 / 512</div>
            </div>
            <div style={{ position:"absolute", bottom:"-14px", left:"18px", background:HC.surface, border:`1px solid ${HC.border}`, borderRadius:"8px", padding:"8px 12px" }}>
              <div style={{ fontSize:"9px", color:HC.textDim, marginBottom:"2px", fontFamily:"'IBM Plex Mono',monospace" }}>SIGNALS TODAY</div>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:"16px", color:HC.text, fontWeight:"600" }}>1,204 flagged</div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* ══ TOP TRADERS — LEADERBOARD ══ */}
      <section id="traders" className="section-pad" style={{ padding:"88px clamp(16px,5vw,60px)", maxWidth:"1240px", margin:"0 auto" }}>
        <Reveal>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", flexWrap:"wrap", gap:"16px", marginBottom:"44px" }}>
            <div>
              <span className="section-label">Leaderboard</span>
              <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(28px,4vw,42px)", fontWeight:"600", letterSpacing:"-0.5px" }}>Top performing traders</h2>
            </div>
            <a href="/market-place" className="btn-secondary" style={{ fontSize:"12px", padding:"10px 20px" }}>View All 2,400+ →</a>
          </div>

          <div style={{ border:`1px solid ${C.text}`, borderRadius:"4px", overflow:"hidden" }}>
            {TRADERS.map((t,idx)=>(
              <div key={t.id} style={{
                display:"grid", gridTemplateColumns:"48px 1.4fr 100px 100px 90px 90px 110px",
                alignItems:"center", gap:"14px", padding:"20px 24px",
                borderBottom: idx<TRADERS.length-1?`1px solid ${C.border}`:"none",
                background: idx%2===1?C.surfaceAlt:C.surface,
              }} className="traders-grid-row">
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:"22px", fontStyle:"italic", color:C.textDim }}>{String(idx+1).padStart(2,"0")}</div>
                <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
                  <div className="avatar" style={{ width:"40px", height:"40px", background:t.bg, fontSize:"13px", color:"#E9EFF6" }}>{t.initials}</div>
                  <div>
                    <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                      <div style={{ fontSize:"14px", fontWeight:"700", color:C.text, fontFamily:"'Fraunces',serif" }}>{t.name}</div>
                      {t.star && <Stamp label="RANK 1" sub="★" size={30} rotate={-12} />}
                    </div>
                    <div style={{ fontSize:"11px", color:C.textDim }}>{t.handle}</div>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize:"9px", color:C.textDim, marginBottom:"2px", fontFamily:"'IBM Plex Mono',monospace" }}>ROI</div>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:"14px", color:C.green, fontWeight:"600" }}>{t.roi}</div>
                </div>
                <div>
                  <div style={{ fontSize:"9px", color:C.textDim, marginBottom:"2px", fontFamily:"'IBM Plex Mono',monospace" }}>MONTHLY</div>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:"14px", color:C.green }}>{t.monthly}</div>
                </div>
                <div>
                  <div style={{ fontSize:"9px", color:C.textDim, marginBottom:"2px", fontFamily:"'IBM Plex Mono',monospace" }}>MAX DD</div>
                  <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:"14px", color:C.red }}>{t.dd}</div>
                </div>
                <Sparkline data={t.spark} up/>
                <a href={`/copy-trading`} className="copy-btn" style={{ textAlign:"center" }}>Copy Trader</a>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ══ FEATURES ══ */}
      <section id="features" style={{ background:C.surface, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}` }}>
        <div className="section-pad" style={{ padding:"88px clamp(16px,5vw,60px)", maxWidth:"1000px", margin:"0 auto" }}>
          <Reveal>
            <div style={{ marginBottom:"36px" }}>
              <span className="section-label">Platform</span>
              <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(28px,4vw,42px)", fontWeight:"600", letterSpacing:"-0.5px", marginBottom:"14px" }}>Everything you need to win</h2>
              <p style={{ fontSize:"15px", color:C.textMid, maxWidth:"520px", lineHeight:"1.7" }}>Professional-grade tools built for investors who take their portfolio seriously.</p>
            </div>
            <div>
              {FEATURES.map((f,i)=>(
                <Reveal key={i} delay={i*60}>
                  <a href={f.href} className="feature-row">
                    <div style={{ width:"42px", height:"42px", borderRadius:"3px", border:`1px solid ${C.text}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"18px", color:C.accent, flexShrink:0, fontFamily:"'Fraunces',serif" }}>{f.icon}</div>
                    <div>
                      <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:"18px", fontWeight:"600", color:C.text, marginBottom:"6px" }}>{f.title}</h3>
                      <p style={{ fontSize:"13.5px", color:C.textMid, lineHeight:"1.7", maxWidth:"520px" }}>{f.desc}</p>
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ MOBILE APP ══ */}
      <section id="mobile-app" style={{ background:C.bg }}>
        <div className="section-pad mobile-grid" style={{ padding:"96px clamp(16px,5vw,60px)", maxWidth:"1180px", margin:"0 auto", display:"grid", gridTemplateColumns:"1fr 1fr", gap:"60px", alignItems:"center" }}>
          <Reveal>
            <div>
              <span className="section-label">Mobile</span>
              <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(28px,4vw,42px)", fontWeight:"600", letterSpacing:"-0.5px", marginBottom:"16px" }}>Your desk, in your pocket</h2>
              <p style={{ fontSize:"15px", color:C.textMid, lineHeight:"1.75", marginBottom:"28px", maxWidth:"460px" }}>
                Every signal, every copied trade, every basis point of P&amp;L — synced live to your phone. Approve trades, adjust risk, and watch the model think from anywhere.
              </p>
              <div style={{ display:"flex", flexDirection:"column", gap:"14px", marginBottom:"32px" }}>
                {[
                  "Push alerts the instant a signal crosses your confidence threshold",
                  "Biometric login and withdrawal address locking, built in",
                  "Full copy-trading controls — pause, resize, or exit a position in two taps",
                ].map((t,i)=>(
                  <div key={i} style={{ display:"flex", gap:"10px", alignItems:"flex-start" }}>
                    <span style={{ color:C.accent, fontFamily:"'Fraunces',serif", fontStyle:"italic", flexShrink:0 }}>✓</span>
                    <span style={{ fontSize:"13.5px", color:C.textMid, lineHeight:"1.6" }}>{t}</span>
                  </div>
                ))}
              </div>
              <div style={{ display:"flex", gap:"12px", flexWrap:"wrap" }}>
                {["Download for iOS","Download for Android"].map(label=>(
                  <a key={label} href="/app" style={{ display:"flex", alignItems:"center", gap:"8px", border:`1px solid ${C.text}`, borderRadius:"8px", padding:"10px 16px", fontSize:"12.5px", fontWeight:"600", color:C.text }}>
                    <span style={{fontSize:"13px"}}>⬇</span> {label}
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <MobileShowcase/>
          </Reveal>
        </div>
      </section>

      {/* ══ HOW IT WORKS ══ */}
      <section id="how-it-works" className="section-pad" style={{ padding:"88px clamp(16px,5vw,60px)", maxWidth:"1240px", margin:"0 auto" }}>
        <Reveal>
          <div style={{ textAlign:"center", marginBottom:"52px" }}>
            <span className="section-label" style={{justifyContent:"center"}}>How It Works</span>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(28px,4vw,42px)", fontWeight:"600", letterSpacing:"-0.5px" }}>Start in three simple steps</h2>
          </div>
          <div className="steps-grid" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"32px", position:"relative" }}>
            <div className="steps-line" style={{ position:"absolute", top:"34px", left:"calc(16.7% + 20px)", right:"calc(16.7% + 20px)", height:"1px", background:C.border }}/>
            {[
              { num:"01", title:"Create Account",  desc:"Sign up in 60 seconds. No credit card required. Full access to the platform immediately." },
              { num:"02", title:"Pick Traders",    desc:"Browse verified traders by ROI, risk profile, and strategy. Filter by asset class or style." },
              { num:"03", title:"Copy & Grow",     desc:"Allocate capital and our engine mirrors trades in real time. Monitor, adjust, or stop anytime." },
            ].map((s,i)=>(
              <Reveal key={i} delay={i*100} style={{ textAlign:"center" }}>
                <div style={{ width:"68px", height:"68px", borderRadius:"50%", background:C.surface, border:`1px solid ${C.text}`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Fraunces',serif", fontSize:"20px", fontStyle:"italic", color:C.accent, margin:"0 auto 20px", position:"relative", zIndex:1 }}>{s.num}</div>
                <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:"19px", fontWeight:"600", color:C.text, marginBottom:"10px" }}>{s.title}</h3>
                <p style={{ fontSize:"13.5px", color:C.textMid, lineHeight:"1.76", maxWidth:"280px", margin:"0 auto" }}>{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ══ TESTIMONIALS — PULL QUOTES ══ */}
      <section id="testimonials" style={{ background:C.text, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}` }}>
        <div className="section-pad" style={{ padding:"88px clamp(16px,5vw,60px)", maxWidth:"1100px", margin:"0 auto" }}>
          <Reveal>
            <div style={{ textAlign:"center", marginBottom:"52px" }}>
              <span className="section-label" style={{justifyContent:"center", color:"#D6A5AA"}}>Testimonials</span>
              <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(28px,4vw,42px)", fontWeight:"600", letterSpacing:"-0.5px", color:C.bg }}>Loved by investors worldwide</h2>
            </div>
            <div className="testi-grid" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"36px" }}>
              {TESTIMONIALS.map((t,i)=>(
                <Reveal key={i} delay={i*100}>
                  <div className="testimonial-block">
                    <div style={{ fontSize:"42px", color:"#D6A5AA", marginBottom:"6px", lineHeight:1, fontFamily:"'Fraunces',serif", fontStyle:"italic" }}>"</div>
                    <p style={{ fontSize:"15px", color:`${C.bg}D8`, lineHeight:"1.75", marginBottom:"22px", fontFamily:"'Fraunces',serif", fontStyle:"italic" }}>{t.quote}</p>
                    <div style={{ display:"flex", alignItems:"center", gap:"10px", borderTop:"1px solid rgba(233,239,246,.18)", paddingTop:"16px" }}>
                      <div className="avatar" style={{ width:"32px", height:"32px", background:t.bg, fontSize:"11px", color:C.bg }}>{t.initials}</div>
                      <div>
                        <div style={{ fontSize:"12px", fontWeight:"600", color:C.bg }}>{t.author}</div>
                        <div style={{ fontSize:"10.5px", color:"rgba(233,239,246,.5)" }}>{t.role}</div>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ HIRE TRADER ══ */}
      <section id="hire-trader" className="section-pad" style={{ padding:"88px clamp(16px,5vw,60px)", maxWidth:"1240px", margin:"0 auto" }}>
        <Reveal>
          <div style={{ textAlign:"center", marginBottom:"52px" }}>
            <span className="section-label" style={{justifyContent:"center"}}>Hire Trader</span>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(28px,4vw,42px)", fontWeight:"600", letterSpacing:"-0.5px" }}>Work with elite traders</h2>
            <p style={{ fontSize:"15px", color:C.textMid, maxWidth:"520px", margin:"14px auto 0", lineHeight:"1.72" }}>Hire a verified professional to manage your portfolio or execute a custom strategy on your behalf.</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"20px" }} className="traders-grid">
            {[
              { initials:"LR", bg:"#33405A", name:"Lena Richter", role:"Macro Strategist", rate:"$1,200/mo", assets:"Forex · Commodities", rating:"4.98", trades:"3.2K", roi:"+289%" },
              { initials:"JO", bg:"#2E4A38", name:"James Okafor", role:"Crypto Quant",    rate:"$950/mo",  assets:"BTC · ETH · Alts",   rating:"4.95", trades:"5.1K", roi:"+412%" },
              { initials:"NP", bg:"#2F5468", name:"Nadia Park",   role:"Equity Analyst",  rate:"$800/mo",  assets:"Stocks · ETFs",       rating:"4.97", trades:"2.8K", roi:"+197%" },
            ].map((t,i)=>(
              <Reveal key={i} delay={i*100}>
                <div className="trader-card" style={{ height:"100%" }}>
                  <div style={{ position:"absolute", top:"14px", right:"14px" }}><Stamp label="VERIFIED" sub="✓" size={40} rotate={10} /></div>
                  <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"18px" }}>
                    <div className="avatar" style={{ width:"46px", height:"46px", background:t.bg, fontSize:"15px", color:"#E9EFF6" }}>{t.initials}</div>
                    <div>
                      <div style={{ fontSize:"15px", fontWeight:"700", color:C.text, fontFamily:"'Fraunces',serif" }}>{t.name}</div>
                      <div style={{ fontSize:"11px", color:C.accent, fontWeight:"600" }}>{t.role}</div>
                    </div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1px", marginBottom:"18px", background:C.border, border:`1px solid ${C.border}` }}>
                    {[{lbl:"All-time ROI",val:t.roi,g:true},{lbl:"Trades",val:t.trades},{lbl:"Rating",val:`${t.rating} ★`},{lbl:"Rate",val:t.rate}].map(m=>(
                      <div key={m.lbl} style={{ background:C.surface, padding:"10px 10px" }}>
                        <div style={{ fontSize:"9px", color:C.textDim, marginBottom:"4px", fontFamily:"'IBM Plex Mono',monospace" }}>{m.lbl.toUpperCase()}</div>
                        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:"13px", color:m.g?C.green:C.text }}>{m.val}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize:"11px", color:C.textDim, marginBottom:"16px" }}>Assets: {t.assets}</div>
                  <a href={`/hire-trader`} className="btn-primary" style={{ width:"100%", justifyContent:"center", fontSize:"13px", padding:"10px" }}>Hire Now →</a>
                </div>
              </Reveal>
            ))}
          </div>
        </Reveal>
      </section>

      <div className="divider"/>

      {/* ══ FORECAST ══ */}
      <section id="forecast" style={{ background:C.surface, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}` }}>
        <div className="section-pad" style={{ padding:"88px clamp(16px,5vw,60px)", maxWidth:"1240px", margin:"0 auto" }}>
          <Reveal>
            <div style={{ textAlign:"center", marginBottom:"52px" }}>
              <span className="section-label" style={{justifyContent:"center"}}>Forecast</span>
              <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(28px,4vw,42px)", fontWeight:"600", letterSpacing:"-0.5px" }}>AI-powered market forecasts</h2>
              <p style={{ fontSize:"15px", color:C.textMid, maxWidth:"520px", margin:"14px auto 0", lineHeight:"1.72" }}>Get predictive signals on major assets powered by our ensemble ML models and sentiment analysis.</p>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"20px" }} className="traders-grid">
              {[
                { sym:"BTC/USD", horizon:"7 Day",  signal:"Bullish",  confidence:"84%", target:"$74,200",  current:"$67,420", col:C.green },
                { sym:"ETH/USD", horizon:"7 Day",  signal:"Neutral",  confidence:"61%", target:"$3,950",   current:"$3,841",  col:C.gold },
                { sym:"SOL/USD", horizon:"14 Day", signal:"Bullish",  confidence:"78%", target:"$220",     current:"$178",    col:C.green },
                { sym:"BNB/USD", horizon:"7 Day",  signal:"Bearish",  confidence:"72%", target:"$548",     current:"$592",    col:C.red },
                { sym:"XRP/USD", horizon:"14 Day", signal:"Bullish",  confidence:"69%", target:"$0.80",    current:"$0.63",   col:C.green },
                { sym:"ADA/USD", horizon:"7 Day",  signal:"Bearish",  confidence:"65%", target:"$0.39",    current:"$0.44",   col:C.red },
              ].map((f,i)=>(
                <Reveal key={i} delay={i*70}>
                  <div className="trader-card" style={{ height:"100%" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"16px" }}>
                      <div>
                        <div style={{ fontFamily:"'Fraunces',serif", fontSize:"17px", fontWeight:"700", color:C.text }}>{f.sym}</div>
                        <div style={{ fontSize:"11px", color:C.textDim }}>{f.horizon} Outlook</div>
                      </div>
                      <div style={{ border:`1px solid ${f.col}`, borderRadius:"2px", padding:"4px 10px", fontSize:"10px", color:f.col, fontWeight:"700", letterSpacing:".06em", fontFamily:"'IBM Plex Mono',monospace" }}>{f.signal.toUpperCase()}</div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1px", marginBottom:"14px", background:C.border, border:`1px solid ${C.border}` }}>
                      <div style={{ background:C.surface, padding:"10px" }}>
                        <div style={{ fontSize:"9px", color:C.textDim, marginBottom:"4px", fontFamily:"'IBM Plex Mono',monospace" }}>CURRENT</div>
                        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:"13px", color:C.text }}>{f.current}</div>
                      </div>
                      <div style={{ background:C.surface, padding:"10px" }}>
                        <div style={{ fontSize:"9px", color:C.textDim, marginBottom:"4px", fontFamily:"'IBM Plex Mono',monospace" }}>TARGET</div>
                        <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:"13px", color:f.col }}>{f.target}</div>
                      </div>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                      <div style={{ fontSize:"11px", color:C.textDim, fontFamily:"'IBM Plex Mono',monospace" }}>CONFIDENCE</div>
                      <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                        <div style={{ width:"80px", height:"3px", background:C.border, overflow:"hidden" }}>
                          <div style={{ width:f.confidence, height:"100%", background:f.col }}/>
                        </div>
                        <span style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:"12px", color:f.col }}>{f.confidence}</span>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ CALENDAR ══ */}
      <section id="calendar" className="section-pad" style={{ padding:"88px clamp(16px,5vw,60px)", maxWidth:"1240px", margin:"0 auto" }}>
        <Reveal>
          <div style={{ textAlign:"center", marginBottom:"52px" }}>
            <span className="section-label" style={{justifyContent:"center"}}>Calendar</span>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(28px,4vw,42px)", fontWeight:"600", letterSpacing:"-0.5px" }}>Economic & earnings calendar</h2>
            <p style={{ fontSize:"15px", color:C.textMid, maxWidth:"520px", margin:"14px auto 0", lineHeight:"1.72" }}>Never miss a market-moving event. Track economic releases, earnings, and central bank decisions.</p>
          </div>
          <div style={{ background:C.surface, border:`1px solid ${C.text}`, borderRadius:"4px", overflow:"hidden" }}>
            {/* Calendar header */}
            <div style={{ padding:"20px 28px", borderBottom:`1px solid ${C.text}`, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"12px" }}>
              <div style={{ fontFamily:"'Fraunces',serif", fontWeight:"600", fontSize:"17px", color:C.text, fontStyle:"italic" }}>June 2025</div>
              <div style={{ display:"flex", gap:"8px" }}>
                {["All","High Impact","Earnings","Central Banks"].map((f,i)=>(
                  <button key={f} style={{ padding:"6px 14px", borderRadius:"2px", fontSize:"11px", fontWeight:"600", border:`1px solid ${i===1?C.accent:C.border}`, background:i===1?C.accentDim:"transparent", color:i===1?C.accent:C.textDim, cursor:"pointer", fontFamily:"'IBM Plex Mono',monospace" }}>{f}</button>
                ))}
              </div>
            </div>
            {/* Events list */}
            <div style={{ overflowX:"auto" }}>
            {[
              { date:"Mon Jun 2",  time:"14:30",  event:"US Non-Farm Payrolls",    impact:"high",   asset:"USD",  forecast:"185K",  prev:"177K" },
              { date:"Tue Jun 3",  time:"10:00",  event:"ISM Services PMI",        impact:"medium", asset:"USD",  forecast:"51.2",  prev:"49.8" },
              { date:"Wed Jun 4",  time:"18:00",  event:"Fed FOMC Minutes",        impact:"high",   asset:"USD",  forecast:"—",     prev:"—" },
              { date:"Thu Jun 5",  time:"12:45",  event:"ECB Rate Decision",       impact:"high",   asset:"EUR",  forecast:"3.25%", prev:"3.50%" },
              { date:"Thu Jun 5",  time:"After",  event:"NVIDIA Earnings (NVDA)",  impact:"high",   asset:"NVDA", forecast:"$0.93", prev:"$0.61" },
              { date:"Fri Jun 6",  time:"08:30",  event:"US CPI (MoM)",            impact:"high",   asset:"USD",  forecast:"0.2%",  prev:"0.3%" },
            ].map((ev,i)=>(
              <div key={i} style={{ display:"grid", gridTemplateColumns:"140px 80px 1fr 100px 90px 90px", alignItems:"center", padding:"16px 28px", borderBottom:i<5?`1px solid ${C.border}`:"none", gap:"12px" }}
                onMouseEnter={e=>{ e.currentTarget.style.background=C.surfaceAlt; }}
                onMouseLeave={e=>{ e.currentTarget.style.background="transparent"; }}
              >
                <div style={{ fontSize:"12px", color:C.textMid, fontFamily:"'IBM Plex Mono',monospace" }}>{ev.date}</div>
                <div style={{ fontSize:"12px", color:C.textDim, fontFamily:"'IBM Plex Mono',monospace" }}>{ev.time}</div>
                <div style={{ fontSize:"13.5px", fontWeight:"600", color:C.text, fontFamily:"'Fraunces',serif" }}>{ev.event}</div>
                <div>
                  <span style={{ display:"inline-block", padding:"3px 10px", fontSize:"10px", fontWeight:"700", fontFamily:"'IBM Plex Mono',monospace",
                    color: ev.impact==="high" ? C.red : C.blue,
                    border:`1px solid ${ev.impact==="high"?C.red:C.blue}`,
                  }}>{ev.impact==="high"?"● HIGH":"● MED"}</span>
                </div>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace", fontSize:"12px", color:C.accent }}>{ev.asset}</div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:"10px", color:C.textDim, marginBottom:"2px", fontFamily:"'IBM Plex Mono',monospace" }}>F: {ev.forecast}</div>
                  <div style={{ fontSize:"10px", color:C.textDim, fontFamily:"'IBM Plex Mono',monospace" }}>P: {ev.prev}</div>
                </div>
              </div>
            ))}
            </div>
            <div style={{ padding:"16px 28px", borderTop:`1px solid ${C.text}`, textAlign:"center" }}>
              <a href="/calendar" style={{ fontSize:"13px", color:C.accent, fontWeight:"600" }}>View Full Calendar →</a>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ══ TRUST & SECURITY ══ */}
      <section id="security" style={{ background:C.surface, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}` }}>
        <div className="section-pad" style={{ padding:"88px clamp(16px,5vw,60px)", maxWidth:"1180px", margin:"0 auto" }}>
          <Reveal>
            <div style={{ textAlign:"center", marginBottom:"52px" }}>
              <span className="section-label" style={{justifyContent:"center"}}>Security</span>
              <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(28px,4vw,42px)", fontWeight:"600", letterSpacing:"-0.5px", marginBottom:"14px" }}>Built on institutional-grade trust</h2>
              <p style={{ fontSize:"15px", color:C.textMid, maxWidth:"560px", margin:"0 auto", lineHeight:"1.7" }}>Your capital and data are protected by the same controls used by regulated financial institutions.</p>
            </div>
          </Reveal>

          <div className="steps-grid" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"28px", marginBottom:"48px" }}>
            {[
              { icon:"⛨", title:"Regulated & Licensed", desc:"Registered as a money services business and held to compliance standards across every jurisdiction we operate in." },
              { icon:"⬡", title:"Bank-Level Encryption", desc:"256-bit AES encryption at rest and TLS 1.3 in transit, with keys stored in hardware security modules." },
              { icon:"◈", title:"Segregated Client Funds", desc:"Client capital sits in segregated accounts, fully separate from operating funds, and is never used for proprietary trading." },
              { icon:"✓", title:"Independent Audits", desc:"Quarterly third-party penetration testing and security audits, with SOC 2 Type II controls in place." },
              { icon:"⚿", title:"Enforced 2FA", desc:"Mandatory two-factor authentication, biometric login, withdrawal address whitelisting, and session anomaly detection." },
              { icon:"◉", title:"99.98% Uptime SLA", desc:"Redundant infrastructure across multiple regions with real-time failover monitoring and status transparency." },
            ].map((s,i)=>(
              <Reveal key={i} delay={i*60}>
                <div style={{ padding:"26px", border:`1px solid ${C.border}`, borderRadius:"6px", height:"100%" }}>
                  <div style={{ width:"42px", height:"42px", borderRadius:"3px", border:`1px solid ${C.text}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"18px", color:C.accent, marginBottom:"16px", fontFamily:"'Fraunces',serif" }}>{s.icon}</div>
                  <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:"17px", fontWeight:"600", color:C.text, marginBottom:"8px" }}>{s.title}</h3>
                  <p style={{ fontSize:"13px", color:C.textMid, lineHeight:"1.72" }}>{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120}>
            <div style={{ display:"flex", flexWrap:"wrap", justifyContent:"center", gap:"12px" }}>
              {["SOC 2 Type II","ISO 27001 Aligned","GDPR Compliant","256-bit AES","Segregated Accounts","2FA Enforced"].map(badge=>(
                <span key={badge} style={{ fontSize:"11px", fontWeight:"600", letterSpacing:".04em", color:C.textMid, border:`1px solid ${C.border}`, borderRadius:"20px", padding:"8px 16px", fontFamily:"'IBM Plex Mono',monospace", background:C.bg }}>{badge}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <div className="divider"/>

      {/* ══ CTA ══ */}
      <section id="cta" className="section-pad" style={{ padding:"88px clamp(16px,5vw,60px)" }}>
        <Reveal>
          <div style={{
            maxWidth:"680px", margin:"0 auto", textAlign:"center",
            background:C.surface, border:`1px solid ${C.text}`,
            borderRadius:"4px", padding:"clamp(36px,6vw,60px) clamp(24px,5vw,52px)",
            position:"relative",
          }}>
            <div style={{ position:"relative", zIndex:1 }}>
              <span className="section-label" style={{marginBottom:"14px", justifyContent:"center"}}>Get Early Access</span>
              <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:"clamp(26px,4vw,40px)", fontWeight:"600", letterSpacing:"-0.5px", color:C.text, marginBottom:"16px" }}>
                Start growing your<br/><span style={{fontStyle:"italic", color:C.accent}}>wealth today</span>
              </h2>
              <p style={{ fontSize:"clamp(13px,1.8vw,15px)", color:C.textMid, lineHeight:"1.72", marginBottom:"34px" }}>
                Join 280,000+ investors on Optionstock. No minimum balance. Cancel anytime.
              </p>

              {submitted ? (
                <div style={{ border:`1px solid ${C.green}`, padding:"16px 24px", fontFamily:"'IBM Plex Mono',monospace", color:C.green, fontSize:"14px" }}>
                  ✓ You're on the list — we'll be in touch soon!
                </div>
              ) : (
                <div className="cta-email-row" style={{ display:"flex", gap:"16px", justifyContent:"center", flexWrap:"wrap", alignItems:"flex-end" }}>
                  <input
                    className="tf-input"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={e=>setEmail(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
                    style={{ maxWidth:"280px" }}
                  />
                  <button className="btn-primary" onClick={handleSubmit} style={{whiteSpace:"nowrap"}}>
                    Get Started Free
                  </button>
                </div>
              )}
              <p style={{ fontSize:"11px", color:C.textDim, marginTop:"18px", fontFamily:"'IBM Plex Mono',monospace" }}>No credit card required · Free forever plan available</p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ══ FOOTER ══ */}
      <footer style={{ borderTop:`1px solid ${C.border}`, padding:"52px clamp(16px,5vw,40px) 32px", background:C.text }}>
        <div style={{ maxWidth:"1240px", margin:"0 auto" }}>
          <div className="footer-grid" style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr", gap:"40px", marginBottom:"48px" }}>
            <div>
              <a href="/" style={{ display:"inline-flex", alignItems:"center" }}>
                <img src={logo} alt="Optionstock" style={{ height:"26px", width:"auto", filter:"invert(1) brightness(2)" }} />
              </a>
              <p style={{ fontSize:"13px", color:"rgba(233,239,246,.55)", lineHeight:"1.72", marginTop:"14px", maxWidth:"240px" }}>
                The professional copy trading platform for modern investors.
              </p>
              <div style={{ display:"flex", gap:"10px", marginTop:"20px" }}>
                {[
                  { key:"x", href:"https://x.com/Optionstock",
                    icon:<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M18.9 2H22l-7.6 8.7L23.3 22h-7l-5.5-7.2L4.5 22H1.3l8.1-9.3L1 2h7.2l5 6.6L18.9 2Zm-1.2 18h1.7L6.4 3.9H4.6L17.7 20Z"/></svg> },
                  { key:"linkedin", href:"https://linkedin.com/company/Optionstock",
                    icon:<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M4.98 3.5A2.5 2.5 0 1 1 5 8.5a2.5 2.5 0 0 1-.02-5ZM2.5 9.75h5V21h-5V9.75ZM10 9.75h4.8v1.54h.07c.67-1.2 2.3-2.47 4.73-2.47 5.06 0 6 3.13 6 7.2V21h-5v-5.6c0-1.34-.02-3.06-1.98-3.06-1.98 0-2.28 1.45-2.28 2.96V21h-5V9.75Z"/></svg> },
                  { key:"discord", href:"https://discord.gg/Optionstock",
                    icon:<svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M20.3 5.3A17.6 17.6 0 0 0 15.9 4l-.3.6c1.7.4 2.6.9 3.5 1.5-1.5-.7-3-1.1-4.5-1.1s-3 .4-4.5 1.1c.9-.6 1.9-1.1 3.5-1.5L13.3 4A17.6 17.6 0 0 0 8.9 5.3C6.4 8.9 5.7 12.4 6 15.8c1.8 1.4 3.6 2.2 5.4 2.7l.7-1.2c-.7-.3-1.4-.6-2-1 .2-.1.3-.3.5-.4 3.9 1.8 8.2 1.8 12.1 0 .1.1.3.3.5.4-.6.4-1.3.7-2 1l.7 1.2c1.8-.5 3.6-1.3 5.4-2.7.4-4-.6-7.4-2.5-10.5ZM11.4 13.9c-.8 0-1.4-.8-1.4-1.7 0-.9.6-1.7 1.4-1.7.8 0 1.5.8 1.4 1.7 0 .9-.6 1.7-1.4 1.7Zm5.3 0c-.8 0-1.4-.8-1.4-1.7 0-.9.6-1.7 1.4-1.7.8 0 1.5.8 1.4 1.7 0 .9-.6 1.7-1.4 1.7Z"/></svg> },
                  { key:"telegram", href:"https://t.me/Optionstock",
                    icon:<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M21.9 4.4 18.6 20c-.2 1-.9 1.2-1.7.8l-4.7-3.5-2.3 2.2c-.3.3-.5.5-1 .5l.3-4.9L18 7.1c.5-.4-.1-.6-.7-.3L6.4 13.2 1.7 11.7c-1-.3-1-1 .2-1.5L20.6 3c.8-.3 1.6.2 1.3 1.4Z"/></svg> },
                  { key:"youtube", href:"https://youtube.com/@Optionstock",
                    icon:<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M23.5 7.2a3 3 0 0 0-2.1-2.1C19.5 4.6 12 4.6 12 4.6s-7.5 0-9.4.5A3 3 0 0 0 .5 7.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 4.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-4.8ZM9.6 15.5V8.5L15.8 12l-6.2 3.5Z"/></svg> },
                ].map(({key, href, icon})=>(
                  <a key={key} href={href} target="_blank" rel="noopener noreferrer" aria-label={key} style={{ width:"34px", height:"34px", borderRadius:"50%", border:`1px solid rgba(233,239,246,.2)`, display:"flex", alignItems:"center", justifyContent:"center", color:"rgba(233,239,246,.65)", transition:"border-color .2s, color .2s, background .2s" }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=C.accent;e.currentTarget.style.background=C.accent;e.currentTarget.style.color=C.bg;}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(233,239,246,.2)";e.currentTarget.style.background="transparent";e.currentTarget.style.color="rgba(233,239,246,.65)";}}
                  >{icon}</a>
                ))}
              </div>
            </div>
            {[
              { title:"Product", links:[
                  { label:"Platform",     href:"/platform" },
                  { label:"Copy Trading", href:"/copy-trading" },
                  { label:"Marketplace",  href:"/market-place" },
              ]},
              { title:"Company", links:[
                  { label:"About",   href:"/about" },
              ]},
              { title:"Legal", links:[
                  { label:"Privacy",       href:"/privacy" },
                  { label:"Terms",         href:"/terms" },
                  { label:"Cookie Policy", href:"/cookie" },
                  { label:"Disclosures",   href:"/disclosures" },
              ]},
            ].map(col=>(
              <div key={col.title}>
                <div style={{ fontSize:"11px", fontWeight:"700", color:"rgba(233,239,246,.4)", letterSpacing:".12em", textTransform:"uppercase", marginBottom:"16px", fontFamily:"'IBM Plex Mono',monospace" }}>{col.title}</div>
                {col.links.map(l=>(
                  <a key={l.label} href={l.href} style={{ display:"block", marginBottom:"10px", fontSize:"13px", color:"rgba(233,239,246,.65)" }}
                     onMouseEnter={e=>e.currentTarget.style.color=C.bg} onMouseLeave={e=>e.currentTarget.style.color="rgba(233,239,246,.65)"}>{l.label}</a>
                ))}
              </div>
            ))}
          </div>

          <div style={{width:"100%", height:"1px", background:"rgba(233,239,246,.15)", marginBottom:"24px"}}/>

          <div className="footer-bottom" style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"10px" }}>
            <p style={{ fontSize:"11.5px", color:"rgba(233,239,246,.4)", fontFamily:"'IBM Plex Mono',monospace" }}>© 2025 Optionstock Inc. All rights reserved. Trading involves risk. Set in Fraunces & Public Sans.</p>
            <div style={{ display:"flex", gap:"20px" }}>
              {[
                { label:"Privacy", href:"/privacy" },
                { label:"Terms",   href:"/terms" },
                { label:"Cookies", href:"/cookie" },
              ].map(l=>(
                <a key={l.label} href={l.href} style={{fontSize:"12px", color:"rgba(233,239,246,.55)"}}>{l.label}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>

      {/* ══ COOKIE BANNER ══ */}
      <CookieBanner/>
    </>
  );
};

export default OptionstockLanding;